import { useState } from 'react';

import { motion } from 'motion/react';
import PropTypes from 'prop-types';

import { AIAgentBanner } from '@src/components/AIAgentBanner';
import { DiagramContainer } from '@src/components/DiagramContainer';
import { ScreenshotGallery } from '@src/components/ScreenshotGallery';
import { Button } from '@src/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Screenshot
import {
  Activity,
  ArrowRight,
  BarChart3,
  Bell,
  Building2,
  CheckCircle,
  Clock,
  Cloud,
  Cpu,
  Eye,
  Heart,
  Info,
  Lock,
  Network,
  Radio,
  Server,
  Shield,
  ShieldAlert,
  Target,
  Users,
  Zap,
} from 'lucide-react';

import xdrDashboard from '@src/assets/screenshots/xdr-dashboard.webp';
import xdrDetectionRules from '@src/assets/screenshots/xdr-detection-rules.webp';
import xdrInvestigationCases from '@src/assets/screenshots/xdr-investigation-cases.webp';
import xdrMitreAttack from '@src/assets/screenshots/xdr-mitre-attack.webp';
import xdrPlaybooks from '@src/assets/screenshots/xdr-playbooks.webp';
import xdrResponseActions from '@src/assets/screenshots/xdr-response-actions.webp';
import xdrSecurityAlerts from '@src/assets/screenshots/xdr-security-alerts.webp';
import AnswerSection from '@src/components/seo/AnswerSection';

const BOSSS_BLUE = '#2973a3';
const RED = '#dc2626';

const integrations = [
  { name: 'Wazuh', icon: Shield, category: 'SIEM' },
  { name: 'Microsoft Sentinel', icon: Cloud, category: 'SIEM' },
  { name: 'Threat Intel Feeds', icon: Target, category: 'Intelligence' },
  { name: 'AWS CloudTrail', icon: Cloud, category: 'Cloud Platform' },
  { name: 'Azure Monitor', icon: Cloud, category: 'Cloud Platform' },
  { name: 'Endpoint Agents', icon: Server, category: 'Endpoint' },
  { name: 'Firewall Logs', icon: Shield, category: 'Network' },
  { name: 'Identity Providers', icon: Users, category: 'Identity' },
];

// Helper function to calculate line endpoints that touch circle circumference
const calculateLineEndpoints = (
  x1,
  y1,
  r1,
  x2,
  y2,
  r2,
) => {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  return {
    x1: x1 + r1 * Math.cos(angle),
    y1: y1 + r1 * Math.sin(angle),
    x2: x2 - r2 * Math.cos(angle),
    y2: y2 - r2 * Math.sin(angle),
  };
};

// Threat Detection Visualization Component
function ThreatDetectionVisualization() {
  const [hoveredNode, setHoveredNode] = useState(null);

  const centerNode = { x: 200, y: 130, r: 35, label: 'XDR Engine' };
  const threatSources = [
    { x: 100, y: 60, r: 25, label: 'Endpoints', color: '#3b82f6' },
    { x: 300, y: 60, r: 25, label: 'Network', color: '#10b981' },
    { x: 60, y: 150, r: 25, label: 'Cloud', color: '#8b5cf6' },
    { x: 340, y: 150, r: 25, label: 'Identity', color: '#f59e0b' },
    { x: 100, y: 220, r: 25, label: 'Email', color: '#ef4444' },
    { x: 300, y: 220, r: 25, label: 'Apps', color: '#06b6d4' },
  ];

  return (
    <DiagramContainer className="lg:max-w-md mx-auto">
      <svg viewBox="0 0 400 280" className="w-full h-auto">
        {/* Connection lines */}
        {threatSources.map((source, i) => {
          const endpoints = calculateLineEndpoints(
            centerNode.x,
            centerNode.y,
            centerNode.r,
            source.x,
            source.y,
            source.r,
          );
          return (
            <motion.line
              strokeWidth="3"
              key={`line-${i}`}
              x1={endpoints.x1}
              y1={endpoints.y1}
              x2={endpoints.x2}
              y2={endpoints.y2}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              stroke={`${source.color}80`}
              transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
            />
          );
        })}

        {/* Central XDR Node */}
        <motion.circle
          r={centerNode.r}
          cx={centerNode.x}
          cy={centerNode.y}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{ filter: 'drop-shadow(0 4px 8px rgba(41, 115, 163, 0.3))' }}
        />

        <motion.text
          fill="white"
          fontSize="10"
          x={centerNode.x}
          fontWeight="bold"
          textAnchor="middle"
          y={centerNode.y - 5}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          XDR
        </motion.text>

        <motion.text
          fill="white"
          fontSize="8"
          x={centerNode.x}
          textAnchor="middle"
          y={centerNode.y + 8}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.9 }}
        >
          Engine
        </motion.text>

        {/* Source Nodes */}
        {threatSources.map((source, i) => (
          <g key={i}>
            <motion.circle
              r={source.r}
              fill="white"
              cx={source.x}
              cy={source.y}
              strokeWidth="2"
              stroke={source.color}
              initial={{ scale: 0 }}
              transition={{ duration: 0.5, delay: 0.7 + i * 0.1 }}
              animate={{ scale: hoveredNode === source.label ? 1.1 : 1 }}
              style={{
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                cursor: 'pointer',
              }}
              onMouseLeave={() => setHoveredNode(null)}
              onMouseEnter={() => setHoveredNode(source.label)}
            />

            <motion.text
              x={source.x}
              fontSize="8"
              y={source.y + 4}
              fontWeight="600"
              textAnchor="middle"
              fill={source.color}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1.2 + i * 0.1 }}
            >
              {source.label}
            </motion.text>
          </g>
        ))}
      </svg>
    </DiagramContainer>
  );
}

// MITRE ATT&CK Visualization Component
function MitreAttackVisualization() {
  const tactics = [
    { name: 'Initial Access', color: '#ef4444', coverage: 85 },
    { name: 'Execution', color: '#f97316', coverage: 92 },
    { name: 'Persistence', color: '#eab308', coverage: 78 },
    { name: 'Privilege Esc.', color: '#84cc16', coverage: 88 },
    { name: 'Defense Evasion', color: '#22c55e', coverage: 75 },
    { name: 'Discovery', color: '#14b8a6', coverage: 95 },
    { name: 'Lateral Move.', color: '#06b6d4', coverage: 82 },
    { name: 'Collection', color: '#3b82f6', coverage: 90 },
  ];

  return (
    <DiagramContainer className="lg:max-w-md mx-auto">
      <div className="space-y-3 p-4">
        <div className="text-center mb-4">
          <span className="text-sm font-semibold text-gray-700">MITRE ATT&CK Coverage</span>
        </div>

        {tactics.map((tactic, i) => (
          <motion.div
            key={tactic.name}
            viewport={{ once: true }}
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="flex items-center gap-3"
            transition={{ duration: 0.4, delay: i * 0.05 }}
          >
            <span className="text-xs font-medium text-gray-600 w-24 truncate">{tactic.name}</span>

            <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                viewport={{ once: true }}
                className="h-full rounded-full"
                style={{ backgroundColor: tactic.color }}
                whileInView={{ width: `${tactic.coverage}%` }}
                transition={{ duration: 0.8, delay: 0.5 + i * 0.05 }}
              />
            </div>

            <span className="text-xs font-semibold text-gray-700 w-10">
              {tactic.coverage}
              %
            </span>
          </motion.div>
        ))}
      </div>
    </DiagramContainer>
  );
}

// Alert Timeline Visualization
function AlertTimelineVisualization() {
  const alerts = [
    { time: '14:32', severity: 'critical', label: 'Ransomware Detected', color: '#ef4444' },
    { time: '14:28', severity: 'high', label: 'Suspicious Login', color: '#f97316' },
    { time: '14:15', severity: 'medium', label: 'Port Scan Detected', color: '#eab308' },
    { time: '14:02', severity: 'low', label: 'Policy Violation', color: '#3b82f6' },
  ];

  return (
    <DiagramContainer className="lg:max-w-md mx-auto">
      <div className="space-y-3 p-4">
        <div className="text-center mb-4">
          <span className="text-sm font-semibold text-gray-700">Real-time Alert Feed</span>
        </div>

        {alerts.map((alert, i) => (
          <motion.div
            key={i}
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.1 }}
            className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-100"
          >
            <div
              style={{ backgroundColor: alert.color }}
              className="w-3 h-3 rounded-full animate-pulse"
            />

            <span className="text-xs font-mono text-gray-500">{alert.time}</span>
            <span className="text-sm font-medium text-gray-800 flex-1">{alert.label}</span>

            <span
              className="text-xs font-semibold px-2 py-1 rounded-full"
              style={{
                backgroundColor: `${alert.color}20`,
                color: alert.color,
              }}
            >
              {alert.severity}
            </span>
          </motion.div>
        ))}
      </div>
    </DiagramContainer>
  );
}

// Integration Diagram Component
function IntegrationDiagram({ hoveredIntegration, setHoveredIntegration }) {
  const centerX = 200;
  const centerY = 150;
  const radius = 100;

  const getPosition = (index, total) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    return {
      x: centerX + radius * Math.cos(angle),
      y: centerY + radius * Math.sin(angle),
    };
  };

  return (
    <DiagramContainer className="lg:max-w-md mx-auto">
      <svg viewBox="0 0 400 300" className="w-full h-auto">
        {/* Connection lines */}
        {integrations.map((integration, index) => {
          const pos = getPosition(index, integrations.length);
          const endpoints = calculateLineEndpoints(centerX, centerY, 35, pos.x, pos.y, 25);
          const isHovered = hoveredIntegration === integration.name;

          return (
            <motion.line
              x1={endpoints.x1}
              y1={endpoints.y1}
              x2={endpoints.x2}
              y2={endpoints.y2}
              stroke={BOSSS_BLUE}
              key={integration.name}
              strokeWidth={isHovered ? '3' : '2'}
              initial={{ pathLength: 0, opacity: 0.6 }}
              transition={{ duration: 0.8, delay: 0.5 + index * 0.1 }}
              animate={{
                pathLength: 1,
                opacity: isHovered ? 1 : 0.6,
              }}
            />
          );
        })}

        {/* Central BOSSS Node */}
        <motion.circle
          r="35"
          cx={centerX}
          cy={centerY}
          fill={BOSSS_BLUE}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          style={{ filter: 'drop-shadow(0 4px 8px rgba(41, 115, 163, 0.3))' }}
        />

        <text x={centerX} fill="white" fontSize="12" y={centerY - 5} fontWeight="bold" textAnchor="middle">
          BOSSS
        </text>

        <text x={centerX} fill="white" fontSize="10" y={centerY + 10} textAnchor="middle">
          XDR
        </text>

        {/* Integration Nodes */}
        {integrations.map((integration, index) => {
          const pos = getPosition(index, integrations.length);
          const isHovered = hoveredIntegration === integration.name;

          return (
            <g key={integration.name}>
              <motion.circle
                r="25"
                cx={pos.x}
                cy={pos.y}
                fill="white"
                stroke={BOSSS_BLUE}
                initial={{ scale: 0 }}
                strokeWidth={isHovered ? '3' : '2'}
                animate={{ scale: isHovered ? 1.1 : 1 }}
                transition={{ duration: 0.5, delay: 0.7 + index * 0.1 }}
                style={{
                  filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
                  cursor: 'pointer',
                }}
                onMouseLeave={() => setHoveredIntegration(null)}
                onMouseEnter={() => setHoveredIntegration(integration.name)}
              />

              <text
                x={pos.x}
                fontSize="7"
                y={pos.y + 4}
                fontWeight="600"
                fill={BOSSS_BLUE}
                textAnchor="middle"
              >
                {integration.name.length > 10 ? integration.name.substring(0, 10) + '...' : integration.name}
              </text>
            </g>
          );
        })}
      </svg>
    </DiagramContainer>
  );
}

export function XDRPage() {
  const onNavigateToTop = useNavigateToTop();
  const [hoveredIntegration, setHoveredIntegration] = useState(null);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 lg:py-32" style={{ background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 50%, #fecaca 100%)' }}>
        {/* Animated Geometric Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            className="absolute top-20 left-16"
            animate={{ rotate: [0, 360], y: [0, -20, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" style={{ color: `${BOSSS_BLUE}30` }}>
              <polygon fill="none" strokeWidth="2" stroke="currentColor" points="20,4 32,12 32,28 20,36 8,28 8,12" />
            </svg>
          </motion.div>

          <motion.div
            className="absolute top-60 right-20"
            animate={{ rotate: [360, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" style={{ color: `${BOSSS_BLUE}40` }}>
              <polygon fill="none" strokeWidth="2" stroke="currentColor" points="12,2 19,7 19,17 12,22 5,17 5,7" />
            </svg>
          </motion.div>

          <motion.div
            style={{ backgroundColor: '#ef444420' }}
            animate={{ y: [0, -30, 0], scale: [1, 1.1, 1] }}
            className="absolute bottom-40 left-10 w-16 h-16 rounded-full"
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />

          <motion.div
            style={{ backgroundColor: '#ef444425' }}
            animate={{ y: [0, -25, 0], x: [0, 10, 0] }}
            className="absolute top-32 right-1/3 w-12 h-12 rounded-full"
            transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              initial={{ opacity: 0, x: -50 }}
            >
              <div
                style={{ backgroundColor: '#ef444415', color: '#dc2626' }}
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <ShieldAlert className="w-4 h-4 mr-2" />
                BOSSS Extended Detection & Response
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                From Alert to Action in Minutes, Not Hours
              </h1>

              <p className="text-xl text-gray-600 mb-6 leading-relaxed">
                Detect, investigate, and respond to security threats across your entire environment
                with real-time monitoring and automated incident response powered by AI.
              </p>

              {/* XDR Key Metrics */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center bg-white/80 rounded-lg p-3 border border-gray-100">
                  <p className="text-2xl font-bold text-red-600">20+</p>
                  <p className="text-xs text-gray-500">SIEM/SOC Integrations</p>
                </div>

                <div className="text-center bg-white/80 rounded-lg p-3 border border-gray-100">
                  <p className="text-2xl font-bold text-red-600">&lt;1hr</p>
                  <p className="text-xs text-gray-500">Mean Time to Respond</p>
                </div>

                <div className="text-center bg-white/80 rounded-lg p-3 border border-gray-100">
                  <p className="text-2xl font-bold text-red-600">15+</p>
                  <p className="text-xs text-gray-500">SIEM Integrations</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                    onClick={() => onNavigateToTop('/demo')}
                  >
                    Schedule a Demo
                  </Button>
                </motion.div>

                <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                  <Button
                    variant="outline"
                    className="border-2 border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                    onClick={() => onNavigateToTop('/contact')}
                  >
                    Contact Sales
                  </Button>
                </motion.div>
              </div>
            </motion.div>

            {/* Hero Illustration */}
            <motion.div
              className="relative"
              animate={{ opacity: 1, x: 0 }}
              initial={{ opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <ThreatDetectionVisualization />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path="/platform/xdr" />


      {/* Product Screenshot Section */}
      <section className="py-24 bg-gradient-to-b from-white to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              See It In Action
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real-time threat detection and response with unified visibility across your entire security stack.
            </p>
          </motion.div>

          <ScreenshotGallery
            urlPath="xdr"
            accentColor={RED}
            screenshots={[
              { src: xdrDashboard, label: 'Dashboard', alt: 'BOSSS XDR security dashboard with detection metrics and MITRE ATT&CK coverage' },
              { src: xdrSecurityAlerts, label: 'Alerts', alt: 'BOSSS XDR alert triage queue enriched with affected assets, identities and techniques' },
              { src: xdrInvestigationCases, label: 'Cases', alt: 'BOSSS XDR investigation cases grouping related alerts into a single timeline' },
              { src: xdrMitreAttack, label: 'MITRE ATT&CK', alt: 'BOSSS XDR MITRE ATT&CK coverage heatmap across tactics and techniques' },
              { src: xdrDetectionRules, label: 'Detection Rules', alt: 'BOSSS XDR detection rule library mapped to ATT&CK techniques' },
              { src: xdrResponseActions, label: 'Response Actions', alt: 'BOSSS XDR automated response actions isolating endpoints, quarantining files and collecting memory dumps' },
              { src: xdrPlaybooks, label: 'Playbooks', alt: 'BOSSS XDR response playbooks automating containment and remediation' },
            ]}
          />

          {/* Feature Callouts */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12"
          >
            {[
              { icon: ShieldAlert, label: 'Threat Detection', value: 'Real-time' },
              { icon: Activity, label: 'Event Correlation', value: 'AI-powered' },
              { icon: Bell, label: 'Alert Management', value: 'Prioritized' },
              { icon: Zap, label: 'Response Actions', value: 'Automated' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4 bg-white rounded-xl shadow-md border border-gray-100">
                <item.icon className="w-6 h-6 mx-auto mb-2 text-red-500" />
                <p className="text-lg font-bold text-gray-900">{item.value}</p>
                <p className="text-xs text-gray-500">{item.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Why Choose BOSSS XDR?
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive security monitoring with real-time threat detection and automated response capabilities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Eye,
                title: 'Complete Visibility',
                description: 'Monitor endpoints, network, cloud, and identity from a single unified dashboard.',
                metric: 'Single Pane of Glass',
              },
              {
                icon: Target,
                title: 'MITRE ATT&CK Coverage',
                description: 'Automatic mapping to all 14 MITRE ATT&CK tactics with detection heatmap visualization.',
                metric: '14 Tactics Covered',
              },
              {
                icon: Zap,
                title: 'Automated Response',
                description: 'Execute playbooks automatically to contain threats and reduce mean time to respond.',
                metric: 'MTTR < 1 Hour',
              },
              {
                icon: Clock,
                title: 'Faster Detection',
                description: 'Real-time correlation and alerting reduces mean time to detect (MTTD).',
                metric: 'MTTD < 6 Min',
              },
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full text-center border-gray-200 hover:shadow-xl transition-all duration-300 group">
                    <CardHeader className="pb-4">
                      <div
                        style={{ backgroundColor: '#ef444415' }}
                        className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300"
                      >
                        <IconComponent className="w-8 h-8" style={{ color: '#dc2626' }} />
                      </div>

                      <CardTitle className="text-xl group-hover:text-red-600 transition-colors">
                        {benefit.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        {benefit.description}
                      </p>

                      <div
                        style={{ backgroundColor: `${BOSSS_BLUE}15`, color: BOSSS_BLUE }}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold"
                      >
                        {benefit.metric}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Security Operations Metrics Dashboard Preview */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Real-Time Security Operations Dashboard
            </h2>

            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Track your security posture with live metrics, analyst performance, and threat intelligence.
            </p>
          </motion.div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-12">
            {[
              { label: 'Active Alerts', value: '81', color: '#ef4444', trend: 'down' },
              { label: 'MTTD', value: '8.6min', color: '#22c55e', trend: 'target' },
              { label: 'MTTR', value: '<1hr', color: '#22c55e', trend: 'target' },
              { label: 'Critical Incidents', value: '4', color: '#f59e0b', trend: 'good' },
              { label: 'SLA Compliance', value: '>95%', color: '#22c55e', trend: 'target' },
              { label: 'False Positive Rate', value: '3%', color: '#22c55e', trend: 'down' },
            ].map((metric, index) => (
              <motion.div
                key={index}
                viewport={{ once: true }}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="bg-gray-800 rounded-xl p-4 text-center border border-gray-700"
              >
                <p className="text-gray-400 text-sm mb-1">{metric.label}</p>
                <p className="text-2xl font-bold" style={{ color: metric.color }}>{metric.value}</p>
              </motion.div>
            ))}
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Bell,
                title: 'Alert Management',
                description: 'Prioritized alerts by severity with investigation workflows and one-click actions.',
              },
              {
                icon: BarChart3,
                title: 'Attack Vector Analysis',
                description: 'Track top attack vectors including credential theft, malware, ransomware, and C2 activity.',
              },
              {
                icon: Users,
                title: 'Analyst Performance',
                description: 'Monitor SOC analyst workload, resolution times, and SLA breach metrics.',
              },
              {
                icon: Cloud,
                title: 'Cloud Security Posture',
                description: 'Unified view of cloud resources, identities, and vulnerabilities across providers.',
              },
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-red-500/50 transition-colors"
                >
                  <IconComponent className="w-8 h-8 text-red-400 mb-4" />
                  <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>

          {/* Integration Sync Status */}
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 bg-gradient-to-r from-red-500/20 to-orange-500/20 rounded-xl p-6 border border-red-500/30"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>

                <div>
                  <h4 className="text-white font-semibold">Insights & Recommendations</h4>
                  <p className="text-gray-300 text-sm">Full XDR analytics: MITRE ATT&CK coverage, alert trends, attack vectors, and analyst performance—all live.</p>
                </div>
              </div>

              <Button
                className="bg-red-500 hover:bg-red-600 text-white"
                onClick={() => onNavigateToTop('/demo')}
              >
                See It In Action
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Feature Deep Dive */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-20"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Complete Protection
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover how BOSSS XDR transforms your security operations with comprehensive detection and response capabilities.
            </p>
          </motion.div>

          {/* Feature 1: Real-time Threat Detection */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20"
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Real-time Threat Detection
              </h3>

              <p className="text-lg text-gray-600 mb-6">
                Continuously monitor your entire environment for security threats. Our detection engine correlates
                events across endpoints, network, cloud, and identity systems to identify sophisticated attacks.
              </p>

              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Cross-environment correlation
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Behavioral analytics and anomaly detection
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Threat intelligence integration
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Customizable detection rules
                </li>
              </ul>
            </div>

            <AlertTimelineVisualization />
          </motion.div>

          {/* Feature 2: MITRE ATT&CK Integration */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20"
          >
            <div className="order-2 lg:order-1 relative">
              <MitreAttackVisualization />
            </div>

            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                MITRE ATT&CK Framework Integration
              </h3>

              <p className="text-lg text-gray-600 mb-6">
                Map all detections to the MITRE ATT&CK framework for comprehensive threat analysis.
                Visualize your coverage across all tactics and techniques to identify security gaps.
              </p>

              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Automatic technique mapping
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Coverage gap analysis
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Attack chain visualization
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Priority-based remediation
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Feature 3: Automated Incident Response */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20"
          >
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Automated Incident Response
              </h3>

              <p className="text-lg text-gray-600 mb-6">
                Respond to threats automatically with customizable playbooks. Contain threats,
                isolate compromised systems, and notify stakeholders without manual intervention.
              </p>

              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Automated containment actions
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Customizable response playbooks
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Integration with ITSM for ticket creation
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Notification and escalation workflows
                </li>

                <li className="flex items-center">
                  <CheckCircle className="w-5 h-5 mr-3" style={{ color: '#dc2626' }} />
                  Built-in response actions &amp; Cortex analyzers/responders
                </li>
              </ul>
            </div>

            <div className="relative">
              <Card className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-700">Playbook: Ransomware Response</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">Active</span>
                  </div>

                  {[
                    { step: 1, action: 'Isolate affected endpoint', status: 'complete' },
                    { step: 2, action: 'Block malicious process', status: 'complete' },
                    { step: 3, action: 'Capture forensic data', status: 'running' },
                    { step: 4, action: 'Notify SOC team', status: 'pending' },
                    { step: 5, action: 'Create incident ticket', status: 'pending' },
                  ].map((item, i) => (
                    <motion.div
                      key={i}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      className="flex items-center gap-3"
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                    >
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          item.status === 'complete'
                            ? 'bg-green-500 text-white' :
                            item.status === 'running'
                              ? 'bg-blue-500 text-white animate-pulse' :
                              'bg-gray-200 text-gray-500'
                        }`}
                      >
                        {item.step}
                      </div>

                      <span className={`text-sm ${item.status === 'complete' ? 'text-gray-500 line-through' : 'text-gray-700'}`}>
                        {item.action}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </Card>
            </div>
          </motion.div>

          {/* XDR Requirements Note */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="mb-20 p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-start gap-3"
          >
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />

            <div>
              <p className="text-sm text-blue-800">
                <span className="font-semibold">Deployment Note:</span>
                {' '}
                Full XDR detection and response capabilities require the BOSSS endpoint agent and browser extension to be installed on monitored devices. Contact your administrator or see our deployment guides for installation instructions.
              </p>
            </div>
          </motion.div>

          {/* Feature 4: Integration Ecosystem */}
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
          >
            <div className="order-2 lg:order-1 relative">
              <IntegrationDiagram
                hoveredIntegration={hoveredIntegration}
                setHoveredIntegration={setHoveredIntegration}
              />
            </div>

            <div className="order-1 lg:order-2">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Seamless Integration Ecosystem
              </h3>

              <p className="text-lg text-gray-600 mb-6">
                Connect BOSSS XDR with your existing security stack. Native integrations with SIEM platforms,
                cloud providers, and endpoint solutions for unified security operations.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {integrations.slice(0, 6).map((integration, idx) => {
                  const IconComponent = integration.icon;
                  return (
                    <motion.div
                      key={idx}
                      viewport={{ once: true }}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: idx * 0.05 }}
                      className="flex items-center gap-2 p-2 bg-white rounded-lg shadow-sm border border-gray-100"
                      onMouseLeave={() => setHoveredIntegration(null)}
                      onMouseEnter={() => setHoveredIntegration(integration.name)}
                    >
                      <IconComponent className="w-4 h-4" style={{ color: BOSSS_BLUE }} />
                      <span className="text-sm text-gray-700">{integration.name}</span>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* OT/ICS Security Section */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <div className="inline-flex items-center bg-amber-500/20 text-amber-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-amber-500/30">
              <Radio className="w-4 h-4 mr-2" />
              Critical Infrastructure Security
            </div>

            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              OT/ICS Security — Where IT Meets Industrial Control
            </h2>

            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              BOSSS bridges IT and Operational Technology security in one platform.
              Monitor industrial control systems, SCADA environments, and critical infrastructure
              alongside your enterprise IT — no separate console, no data silos.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              {
                name: 'Dragos',
                desc: 'ICS/OT threat detection and intelligence for industrial environments',
                specialty: 'ICS Threat Intel',
                color: '#f59e0b',
              },
              {
                name: 'Claroty',
                desc: 'Asset discovery and risk management for OT/ICS/IoMT networks',
                specialty: 'Asset Discovery',
                color: '#3b82f6',
              },
              {
                name: 'Nozomi Networks',
                desc: 'Real-time OT and IoT visibility, threat detection and anomaly analysis',
                specialty: 'Anomaly Detection',
                color: '#8b5cf6',
              },
              {
                name: 'MS Defender for IoT',
                desc: 'Agentless OT/IoT monitoring with native Azure and Sentinel integration',
                specialty: 'Agentless Monitoring',
                color: '#06b6d4',
              },
            ].map((vendor, i) => (
              <motion.div
                key={i}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-slate-800/60 rounded-xl p-6 border border-slate-700 hover:border-amber-500/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-bold text-white text-lg">{vendor.name}</span>

                  <span
                    className="text-xs font-semibold px-2 py-1 rounded-full"
                    style={{ backgroundColor: `${vendor.color}20`, color: vendor.color }}
                  >
                    {vendor.specialty}
                  </span>
                </div>

                <p className="text-slate-400 text-sm">{vendor.desc}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {[
              {
                icon: Cpu,
                title: 'Unified IT + OT Dashboard',
                desc: 'See IT alerts and OT anomalies side-by-side. No context-switching between separate platforms.',
              },
              {
                icon: Network,
                title: 'Protocol-Aware Detection',
                desc: 'Supports Modbus, DNP3, EtherNet/IP, OPC-UA, and other industrial protocols natively through vendor integrations.',
              },
              {
                icon: ShieldAlert,
                title: 'Cross-Domain Incident Response',
                desc: 'When an OT threat is detected, automatically escalate to the XDR case management and GRC incident workflows.',
              },
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 bg-slate-800/40 rounded-xl border border-slate-700">
                <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-amber-400" />
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-1">{item.title}</h4>
                  <p className="text-slate-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-16"
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Industry-Specific Security Solutions
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              BOSSS XDR adapts to the unique security requirements of your industry.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Building2,
                title: 'Financial Services',
                description: 'PCI DSS compliance monitoring, fraud detection, and transaction security.',
                color: 'from-blue-500 to-blue-600',
              },
              {
                icon: Heart,
                title: 'Healthcare',
                description: 'HIPAA compliance, PHI protection, and medical device security monitoring.',
                color: 'from-red-500 to-red-600',
              },
              {
                icon: Lock,
                title: 'Government',
                description: 'FedRAMP compliance, classified data protection, and insider threat detection.',
                color: 'from-purple-500 to-purple-600',
              },
            ].map((industry, index) => {
              const IconComponent = industry.icon;
              return (
                <motion.div
                  key={index}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="h-full border-gray-200 hover:shadow-xl transition-all duration-300 group overflow-hidden">
                    <div className={`h-2 bg-gradient-to-r ${industry.color}`} />

                    <CardHeader>
                      <div className={`w-14 h-14 bg-gradient-to-br ${industry.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>

                      <CardTitle className="text-xl">{industry.title}</CardTitle>
                    </CardHeader>

                    <CardContent>
                      <p className="text-gray-600">{industry.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AI Agent Banner */}
      <AIAgentBanner
        toolsCount={57}
        agentName="XDR Agent"
        accentColor="#ef4444"
        description="Your AI-powered threat detection copilot with 57 specialized tools covering alerts, cases, forensics, playbooks, and response actions. Ask about incidents, alerts, or threat intelligence—get instant answers."
        exampleQueries={[
          '"Are there any active security incidents?"',
          '"Show me alerts from the last 24 hours"',
          '"What IOCs were detected this week?"',
          '"Which devices have critical vulnerabilities?"',
        ]}
      />

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-red-500 to-red-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Security Operations?
            </h2>

            <p className="text-xl text-red-100 mb-8">
              See how BOSSS XDR can help you detect and respond to threats faster.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  className="bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold"
                  onClick={() => onNavigateToTop('/demo')}
                >
                  Schedule a Demo
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>

              <motion.div whileTap={{ scale: 0.95 }} whileHover={{ scale: 1.05 }}>
                <Button
                  variant="outline"
                  className="border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 px-8 py-4 text-lg rounded-lg transition-all duration-200 hover:shadow-xl font-semibold backdrop-blur-sm"
                  onClick={() => onNavigateToTop('/contact')}
                >
                  Contact Sales
                </Button>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

XDRPage.propTypes = {
  onNavigate: PropTypes.func,
};

export default XDRPage;
